/**
 * Date-only values (contract start, end, renewal and notice deadline, cost record dates, license
 * expiration, milestones) are calendar days (#65). The API stores them as UTC midnight of the day
 * (older rows saved by the previous form carry the user's local midnight, 05:00Z in Central, whose
 * UTC day is the same day), so the day is the `YYYY-MM-DD` prefix of the ISO string.
 *
 * Never pass such a value through `new Date(iso)`: west of UTC that instant is the previous evening.
 * Read it with `parseCalendarDate` (a local Date at midnight of that day, which date pickers and
 * date-fns format as the right day) and write it with `toCalendarIso`.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/** The `YYYY-MM-DD` day of an ISO date string, or '' when it has none. */
export function calendarDay(value: string | null | undefined): string {
  if (!value) return '';
  const match = ISO_DAY.exec(value);
  if (!match) return '';
  // Rejects 2027-02-30 and similar, which the Date constructor would roll over. Checked in UTC, so a
  // day the viewer's zone skipped (Pacific/Apia, 2011-12-30) still counts as a real calendar day.
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (date.getUTCFullYear() !== Number(match[1]) || date.getUTCMonth() !== Number(match[2]) - 1 || date.getUTCDate() !== Number(match[3])) {
    return '';
  }
  return `${match[1]}-${match[2]}-${match[3]}`;
}

/** Local midnight of the calendar day an ISO date string names, or null when it has none. */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  const day = calendarDay(value);
  if (!day) return null;
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

/** The `YYYY-MM-DD` of a local Date's own calendar day (the day a date picker shows). */
export function localCalendarDay(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** UTC midnight of a `YYYY-MM-DD` day, the form the API stores: `2027-06-30T00:00:00Z`. */
export function calendarDayToIso(day: string): string {
  return `${day}T00:00:00Z`;
}

/** UTC midnight of the calendar day a local Date shows, for sending a picked date to the API. */
export function toCalendarIso(date: Date): string {
  return calendarDayToIso(localCalendarDay(date));
}

/** Today's calendar day where the user is, as `YYYY-MM-DD` (not the UTC day, which runs ahead in the evening). */
export function todayCalendarDay(now: Date = new Date()): string {
  return localCalendarDay(now);
}

/** Local midnight of today: the default for a new date field. */
export function todayLocalMidnight(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
